import constants from "../api/constants";

// Genuinely-ended subscriptions: the trial lapsed without converting, or the subscription
// is over. These are the only two states that end access.
const ENDED_SUBSCRIPTION_STATUSES = new Set([constants.SUBSCRIPTION_STATUS.TRIAL_EXPIRED, constants.SUBSCRIPTION_STATUS.CANCELLED]);

// LSP-19341: a failed payment never ends access, however long it has been failing. Stripe
// past_due / unpaid / incomplete all map to pendingPayment and paused maps to suspended, so
// this set covers "an established customer's renewal card failed" as well as "the first
// charge after a trial never landed" — mapStripeSubscriptionStatusToAccountStatus cannot tell
// them apart, and neither is an ended subscription.
//
// contact-api still stamps a 21-day pendingPaymentGraceEndDate and still leaves
// organization.statusOverride untouched, but nothing reads that date as a gate any more:
// LSP-19327 stopped gating these statuses on sight (Cannon Moving & Storage, 370121e8, was
// locked out of a paid account for ~5 days) and LSP-19341 removed the day-21 cutoff behind
// it. A failed payment is now chased by the TrialTopBar banner and the payment-failure email,
// never by taking the product away.
const NON_GATING_BILLING_STATUSES = new Set([constants.SUBSCRIPTION_STATUS.PENDING_PAYMENT, constants.SUBSCRIPTION_STATUS.SUSPENDED]);

function hasFreeTrialEnded(organization, subscription) {
	if (window.env.VITE_ENABLE_PURCHASE_MODULE === "false" || !organization) {
		return false;
	}

	// Subscription is the source of truth: an active/trialing subscription always unlocks,
	// even if org flags are stale from a webhook delay (LSP-15991).
	if (
		subscription?.accountStatus === constants.SUBSCRIPTION_STATUS.TRIAL ||
		subscription?.accountStatus === constants.SUBSCRIPTION_STATUS.ACTIVE
	) {
		return false;
	}

	// organization.isTrial is sticky — it means "came through the trial flow", not "is
	// currently trialing" (see organizations.ts) — so it is true for established paying
	// customers too. Everything below must therefore be a real end-of-access signal.
	if (organization.isTrial) {
		// Symmetric with the unlock above: ended subscription statuses always gate trial orgs,
		// even if org.isSubscriptionActive is stale-true or end-flags haven't synced yet (LSP-16543).
		if (ENDED_SUBSCRIPTION_STATUSES.has(subscription?.accountStatus)) {
			return true;
		}

		// Billing needs attention, but it never ends access. Returning here is load-bearing
		// rather than incidental: it bypasses the org-flag fallback below, whose flags describe
		// the trial the customer came through. `isEndedTrial && !isSubscriptionActive` is the
		// ordinary shape for exactly these past-due customers, so falling through would re-gate
		// them through a different door.
		if (NON_GATING_BILLING_STATUSES.has(subscription?.accountStatus)) {
			return false;
		}

		if (
			(organization.isEndedTrial && !organization.isSubscriptionActive) ||
			(organization.isTrialCCNow && !organization.isSubscriptionActive)
		) {
			return true;
		}
	}

	return false;
}

export { hasFreeTrialEnded };
